/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { fetchDatacenterPerformanceAPI } from 'kontrolEdge/components/DataCenter/requests/request';

interface BandwidthItem {
  time: string;
  value: number;
}

interface BandwidthUtilization {
  inbound: BandwidthItem[];
  outbound: BandwidthItem[];
  timeRange: string;
  interval: string;
  unit: string;
}

interface DatacenterPerformanceData {
  datacenterId: number;
  bandwidthUtilization: BandwidthUtilization;
  portA: {
    subId: string,
  },
  portB: {
    subId: string,
  }
}

// Extend the state to include new properties
interface DatacenterState {
  lag: any;
  performanceData: DatacenterPerformanceData | null;
  performanceLoading: boolean;
  performanceError: string | null;
  timeRange: string;
  interval: string;
  // Custom date range mode
  useCustomDateRange: boolean;
  customStartDate: string | null; // ISO string
  customEndDate: string | null;   // ISO string
  maxPoints: number;
}

const initialState: DatacenterState = {
  lag: null,
  performanceData: null,
  performanceLoading: false,
  performanceError: null,
  timeRange: '5m',
  interval: '5s',
  useCustomDateRange: false,
  customStartDate: null,
  customEndDate: null,
  maxPoints: 180,
};

const datacenterSlice = createSlice({
  name: 'datacenter',
  initialState,
  reducers: {
    setLag: (state, action: PayloadAction<any>) => {
      state.lag = action.payload;
    },
    clearLag: (state) => {
      state.lag = null;
    },
    // Reducers for updating filters
    setTimeRange: (state, action: PayloadAction<string>) => {
      state.timeRange = action.payload;
    },
    setInterval: (state, action: PayloadAction<string>) => {
      state.interval = action.payload;
    },
    // Custom date range reducers
    setUseCustomDateRange: (state, action: PayloadAction<boolean>) => {
      state.useCustomDateRange = action.payload;
      if (!action.payload) {
        state.customStartDate = null;
        state.customEndDate = null;
      }
    },
    setCustomDateRange: (state, action: PayloadAction<{ start: string; end: string }>) => {
      state.customStartDate = action.payload.start;
      state.customEndDate = action.payload.end;
    },
    clearCustomDateRange: (state) => {
      state.customStartDate = null;
      state.customEndDate = null;
    },
    // Paints the last answer for the same chart and range while the fetch refreshes it,
    // so switching back to a view does not wait on the chart again (project 13D).
    primePerformanceData: (state, action: PayloadAction<DatacenterPerformanceData>) => {
      state.performanceData = action.payload;
      state.performanceError = null;
    },
    setMaxPoints: (state, action: PayloadAction<number>) => {
      state.maxPoints = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDatacenterPerformance.pending, (state) => {
        state.performanceLoading = true;
        state.performanceError = null;
      })
      .addCase(fetchDatacenterPerformance.fulfilled, (state, action: PayloadAction<DatacenterPerformanceData>) => {
        state.performanceData = action.payload;
        state.performanceLoading = false;
      })
      .addCase(fetchDatacenterPerformance.rejected, (state, action) => {
        state.performanceLoading = false;
        state.performanceError = action.payload as string;
      })
    }
    
});

export const fetchDatacenterPerformance = createAsyncThunk<
DatacenterPerformanceData,
  { 
    api: AxiosInstance; 
    datacenterId: string; 
    timeRange?: string; 
    interval: string; 
    tenantId: number;
    startDate?: string;  // ISO string
    endDate?: string;    // ISO string
    maxPoints?: number;
  },
  { rejectValue: string }
>(
  'internet/fetchPerformance',
  async ({ api, datacenterId, timeRange, interval, tenantId, startDate, endDate, maxPoints }, { rejectWithValue }) => {
    try {
      const response = await fetchDatacenterPerformanceAPI(
        api, 
        datacenterId, 
        timeRange, 
        interval, 
        tenantId,
        startDate,
        endDate,
        maxPoints
      );
      return response.data.data as DatacenterPerformanceData;
    } catch (err) {
      console.error("Error fetching performance data", err);
      return rejectWithValue("Failed to fetch performance data");
    }
  }
);

export const { 
  primePerformanceData,
  setLag, 
  clearLag, 
  setInterval, 
  setTimeRange,
  setUseCustomDateRange,
  setCustomDateRange,
  clearCustomDateRange,
  setMaxPoints
} = datacenterSlice.actions;
export default datacenterSlice.reducer;